import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "@/lib/toast";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useAdminRole } from "@/hooks/useAdminRole";
import { useModeratorStatus } from "@/hooks/useModeratorStatus";
import { Icon } from "@/components/primitives";
import { useTheme } from "@/context/ThemeContext";
import { ThemeStudio } from "@/components/ThemeStudio";
import { AvatarCropper } from "@/components/AvatarCropper";
import { MobileFrame } from "@/components/MobileFrame";
import { SubscriptionTiers } from "@/components/SubscriptionTiers";
import { InterestsEditor } from "@/components/InterestsEditor";
import { LookingForEditor } from "@/components/LookingForEditor";
import { INTERESTS } from "@/lib/interests";
import {
  MARKETPLACE_NAMES,
  ensureUrlProtocol,
  isValidUrl,
  parseMarketplaceLinks,
  urlDomainWarning,
  urlPrefixFor,
  type MarketplaceLink,
} from "@/lib/marketplaces";

// ─────────────────────────────────────────────────────────────────────────────
// Settings — full page replacement for the old SettingsSheet bottom sheet.
// Self-loads the signed-in user's avatar URL + marketplace_profiles array
// from `profiles` so callers can just `nav("/settings")` without piping data.
// Sections (top → bottom): Theme, Profile picture, Email, Your shops, Account.
// ─────────────────────────────────────────────────────────────────────────────

export default function Settings() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, signOut } = useAuth();
  const { isAdmin } = useAdminRole();
  const { data: modState } = useModeratorStatus();

  // Active tab. Synced to the URL via `?tab=` so that navigating to a
  // sub-page (e.g. /settings/notifications, opened from the Account tab) and
  // pressing back returns the user to the same tab — instead of always
  // dumping them on Theme.
  type TabId = "theme" | "profile" | "tips" | "subs" | "account";
  const initialTab = (searchParams.get("tab") as TabId | null);
  const [tab, setTabState] = useState<TabId>(
    initialTab === "theme" || initialTab === "profile" || initialTab === "tips" || initialTab === "subs" || initialTab === "account"
      ? initialTab
      : "theme",
  );
  // Wrapper that keeps the URL in lockstep with the tab state. `replace` so
  // tab clicks don't pile up history entries (only the original /settings
  // entry stays, with its `?tab=` param overwritten).
  const setTab = (next: TabId) => {
    setTabState(next);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", next);
    setSearchParams(nextParams, { replace: true });
  };

  // Loaded from profiles on mount.
  const [avatar, setAvatar] = useState("");
  const [links, setLinks] = useState<MarketplaceLink[]>([]);
  // Username editing
  const [handle, setHandle] = useState("");
  const [savedHandle, setSavedHandle] = useState(""); // confirmed value in DB
  const [editingHandle, setEditingHandle] = useState(false);
  const [handleStatus, setHandleStatus] = useState<"idle" | "checking" | "available" | "taken" | "invalid">("idle");
  const [savingHandle, setSavingHandle] = useState(false);
  const handleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const HANDLE_RE = /^[a-z0-9_]{3,20}$/;
  const [allowTraining, setAllowTraining] = useState(false);
  const [savingTraining, setSavingTraining] = useState(false);
  // Private account toggle. When on, only followers can see the user's posts.
  const [isPrivate, setIsPrivate] = useState(false);
  const [savingPrivate, setSavingPrivate] = useState(false);
  // User's free-text wishlist (profiles.looking_for). Capped to 10 client-side
  // and DB-side; saved on blur via the same row used for other profile fields.
  const [lookingFor, setLookingFor] = useState<string[]>([]);
  const [savedLookingFor, setSavedLookingFor] = useState<string[]>([]);
  const [savingLookingFor, setSavingLookingFor] = useState(false);

  // User's interests (profiles.tags) — surfaced in the Profile tab so they
  // can be edited without bouncing back to the profile page.
  const [interests, setInterests] = useState<string[]>([]);
  const [editingInterests, setEditingInterests] = useState(false);
  // Location settings — the master on/off plus the human-readable "home base"
  // string. The lat/lng/city columns live on the profile but are only set when
  // the user explicitly captures a fix from this page (or Tutorial), so we
  // surface their presence as a status line rather than a separate input.
  const [locationEnabled, setLocationEnabled] = useState(true);
  const [savingLocationEnabled, setSavingLocationEnabled] = useState(false);
  const [homeBase, setHomeBase] = useState("");
  const [savedHomeBase, setSavedHomeBase] = useState("");
  const [savingHomeBase, setSavingHomeBase] = useState(false);
  // Minimal local mirrors of profile lat/lng/city — only used by the
  // location-sharing toggle to clear stale GPS data when the user turns
  // location off. The Captured Fix UI is gone; home base is typed directly.
  const [, setProfileCity] = useState<string | null>(null);
  const [, setProfileLat] = useState<number | null>(null);
  const [, setProfileLng] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);

  // Scroll to a `#section` anchor when present in the URL (e.g.
  // /settings?tab=profile#shops from the Profile page's Edit shops button).
  // Runs after the active tab is in the DOM AND after initial data loads
  // so the target node has its final size before we scroll.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    // Defer one frame so the just-rendered tab content is in the DOM.
    const id = window.requestAnimationFrame(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(id);
    // Re-run after `loaded` flips so the shops list (which only renders
    // once profile data arrives) is in place when we scroll.
  }, [tab, loaded]);

  // Avatar UI
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Email UI
  const [newEmail, setNewEmail] = useState("");
  const [updatingEmail, setUpdatingEmail] = useState(false);

  // Marketplaces UI
  const [savingLinks, setSavingLinks] = useState(false);

  // Suggestions UI — "Help improve Fig" submission box.
  const [suggestionCategory, setSuggestionCategory] = useState<"idea" | "bug" | "other">("idea");
  const [suggestionBody, setSuggestionBody] = useState("");
  const [submittingSuggestion, setSubmittingSuggestion] = useState(false);

  const submitSuggestion = async () => {
    const body = suggestionBody.trim();
    if (!user) { toast.error("Sign in to send a suggestion."); return; }
    if (body.length < 5) { toast.error("Add a bit more detail (5+ chars)."); return; }
    if (body.length > 2000) { toast.error("Keep it under 2000 characters."); return; }
    setSubmittingSuggestion(true);
    const { error } = await supabase
      .from("feedback_suggestions")
      .insert({ user_id: user.id, category: suggestionCategory, body });
    setSubmittingSuggestion(false);
    if (error) { toast.error("Couldn't send. Try again."); return; }
    setSuggestionBody("");
    setSuggestionCategory("idea");
    toast.success("Thanks — we read every one.");
  };

  // Pull current avatar + marketplace profiles once. Cast through unknown
  // because the regenerated supabase types may not yet include the
  // marketplace_profiles column.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("avatar, handle, marketplace_profiles, allow_training_use, is_private, tags, looking_for, location, lat, lng, city, location_enabled" as never)
        .eq("id", user.id)
        .maybeSingle();
      if (cancelled) return;
      const row = data as unknown as {
        avatar?: string;
        handle?: string;
        marketplace_profiles?: unknown;
        allow_training_use?: boolean;
        is_private?: boolean;
        tags?: string[];
        looking_for?: string[];
        location?: string | null;
        lat?: number | null;
        lng?: number | null;
        city?: string | null;
        location_enabled?: boolean;
      } | null;
      setAvatar(row?.avatar ?? "");
      if (row?.handle) { setHandle(row.handle); setSavedHandle(row.handle); }
      setLinks(parseMarketplaceLinks(row?.marketplace_profiles));
      setAllowTraining(!!row?.allow_training_use);
      setIsPrivate(!!row?.is_private);
      setInterests(Array.isArray(row?.tags) ? row!.tags! : []);
      const lf = Array.isArray(row?.looking_for) ? row!.looking_for! : [];
      setLookingFor(lf);
      setSavedLookingFor(lf);
      setHomeBase(row?.location ?? "");
      setSavedHomeBase(row?.location ?? "");
      setProfileCity(row?.city ?? null);
      setProfileLat(row?.lat ?? null);
      setProfileLng(row?.lng ?? null);
      // Default true so users who pre-date the column don't appear to have
      // it turned off (their saved fix would suddenly look "frozen").
      setLocationEnabled(row?.location_enabled !== false);
      setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [user]);

  // ── Username handlers ────────────────────────────────────────────────
  const checkHandle = (raw: string) => {
    const val = raw.toLowerCase().replace(/[^a-z0-9_]/g, "");
    setHandle(val);
    if (handleTimer.current) clearTimeout(handleTimer.current);
    if (!val) { setHandleStatus("idle"); return; }
    if (!HANDLE_RE.test(val)) { setHandleStatus("invalid"); return; }
    setHandleStatus("checking");
    handleTimer.current = setTimeout(async () => {
      if (!user) return;
      const { data } = await supabase
        .from("profiles").select("id").eq("handle", val)
        .neq("id", user.id).maybeSingle();
      setHandleStatus(data ? "taken" : "available");
    }, 350);
  };

  const saveHandle = async () => {
    if (!user || handleStatus === "taken" || handleStatus === "invalid" || handleStatus === "checking") return;
    if (handle.length < 3) { toast.error("Username must be at least 3 characters"); return; }
    setSavingHandle(true);
    // Use upsert so if the profile row is missing (e.g. Google OAuth new account)
    // it gets created rather than silently updating 0 rows.
    const { error } = await supabase.from("profiles")
      .upsert({ id: user.id, handle, display_name: handle } as never, { onConflict: "id" });
    setSavingHandle(false);
    if (error) { toast.error(`Couldn't update username: ${error.message}`); return; }
    setSavedHandle(handle);
    setHandleStatus("idle");
    setEditingHandle(false);
    toast.success("Username updated.");
  };

  // ── Avatar handlers ──────────────────────────────────────────────────
  const onPickAvatarFile = (file: File) => {
    if (!user) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error("Image is too large (max 20MB)"); return; }
    setPendingAvatarFile(file);
  };

  const onAvatarConfirm = async (cropped: Blob) => {
    if (!user) return;
    setUploadingAvatar(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const ext = cropped.type === "image/png" ? "png" : "jpg";
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
      const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
      const res = await fetch(`${supabaseUrl}/functions/v1/upload-avatar`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${session?.access_token}`,
          "apikey": anonKey,
          "x-avatar-ext": ext,
          "Content-Type": cropped.type || "image/jpeg",
        },
        body: cropped,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.error) {
        const msg = json.error ?? `Upload failed (${res.status})`;
        console.error("[avatar] upload failed", res.status, json);
        toast.error(msg);
        return;
      }
      setAvatar(json.url);
      toast.success("Profile picture updated");
    } catch (e) {
      console.error("[avatar] unexpected error", e);
      toast.error(e instanceof Error ? e.message : "Couldn't upload photo");
    } finally {
      setUploadingAvatar(false);
      setPendingAvatarFile(null);
    }
  };

  // ── Email handler ────────────────────────────────────────────────────
  const updateEmail = async () => {
    const next = newEmail.trim().toLowerCase();
    if (!next || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(next)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (next === user?.email?.toLowerCase()) {
      toast.info("That's already your email");
      return;
    }
    setUpdatingEmail(true);
    const { error } = await supabase.auth.updateUser({ email: next });
    setUpdatingEmail(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Check your inbox", {
      description: `We sent a confirmation link to ${next}. Your email changes once you click it.`,
    });
    setNewEmail("");
  };

  // ── Marketplace profile handlers ─────────────────────────────────────
  const addLink = (name: string) => {
    if (links.some((l) => l.name.toLowerCase() === name.toLowerCase())) {
      toast.info(`${name} already added`);
      return;
    }
    // Prefill with the marketplace's canonical shop-URL prefix so the
    // collector only needs to type their username (e.g. "username" → full URL).
    setLinks((cur) => [...cur, { name, url: urlPrefixFor(name) }]);
  };
  const updateLinkUrl = (idx: number, url: string) =>
    setLinks((cur) => cur.map((l, i) => (i === idx ? { ...l, url } : l)));
  const removeLink = (idx: number) =>
    setLinks((cur) => cur.filter((_, i) => i !== idx));

  const saveLinks = async () => {
    if (!user) return;
    // In-Person can be a typed address (no URL parse) OR a Maps share URL —
    // skip URL validation for that row. Personal Site + every marketplace
    // are full URLs and still validated.
    const bad = links.find((l) => {
      if (!l.url) return false;
      if (l.name.toLowerCase() === "in person") return false;
      return !isValidUrl(l.url);
    });
    if (bad) {
      toast.error(`That ${bad.name} link doesn't look like a URL`);
      return;
    }
    const cleaned = links
      .filter((l) => {
        const u = l.url.trim().replace(/\/+$/, "");
        const prefix = urlPrefixFor(l.name).replace(/\/+$/, "");
        // Drop empty rows AND rows where the user added a shop but never
        // appended a username after the prefilled prefix.
        if (u.length === 0) return false;
        if (prefix && u.toLowerCase() === prefix.toLowerCase()) return false;
        return true;
      })
      .map((l) => {
        const lower = l.name.toLowerCase();
        // In Person can be an address ("123 Main St") or a maps URL. Detect
        // the URL case and protocol-fix it; leave plain text untouched so
        // ensureUrlProtocol doesn't produce nonsense like "https://123 Main St".
        if (lower === "in person") {
          const looksLikeUrl = /^https?:\/\//i.test(l.url.trim()) ||
            /^(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/|$)/i.test(l.url.trim());
          return { name: l.name, url: looksLikeUrl ? ensureUrlProtocol(l.url) : l.url.trim() };
        }
        return { name: l.name, url: ensureUrlProtocol(l.url) };
      });

    setSavingLinks(true);
    const { error } = await supabase
      .from("profiles")
      .update({ marketplace_profiles: cleaned } as unknown as Record<string, never>)
      .eq("id", user.id);
    setSavingLinks(false);
    if (error) { toast.error("Couldn't save shops"); return; }
    setLinks(cleaned);
    toast.success(cleaned.length === 0 ? "Shops cleared" : "Shops saved");
  };

  const remaining = MARKETPLACE_NAMES.filter(
    (m) => !links.some((l) => l.name.toLowerCase() === m.toLowerCase()),
  );

  const handleSignOut = async () => {
    await signOut();
  };

  const [deletingAccount, setDeletingAccount] = useState(false);
  const handleDeleteAccount = async () => {
    if (!window.confirm("Permanently delete your account and all your data? This cannot be undone.")) return;
    if (!window.confirm("Are you sure? All your scans, posts, and profile will be gone forever.")) return;
    setDeletingAccount(true);
    const { data: { session } } = await supabase.auth.getSession();
    const { error } = await supabase.functions.invoke("delete-account", {
      headers: { Authorization: `Bearer ${session?.access_token}` },
    });
    if (error) {
      toast.error("Couldn't delete account. Contact support@figverified.com.");
      setDeletingAccount(false);
      return;
    }
    await signOut();
  };

  // Share your profile — fires the native share sheet (iOS / web) with the
  // current user's profile URL. Previously this navigated to /share, which is
  // the per-scan share-card page; with no scan state passed it fell back to
  // the most recent scan in scanStore, so the artifact rendered as the user's
  // last item instead of a profile share. Matches Profile.tsx's handleShareProfile.
  const handleShare = async () => {
    if (!savedHandle) {
      toast.error("Sign in and pick a handle before sharing.");
      return;
    }
    const url = `${window.location.origin}/profile/${savedHandle}`;
    const navAny = navigator as unknown as {
      share?: (data: { title?: string; url?: string }) => Promise<void>;
    };
    if (navAny.share) {
      try {
        await navAny.share({ title: `@${savedHandle} on FIG`, url });
        return;
      } catch {
        // user cancelled — fall through to clipboard
      }
    }
    await navigator.clipboard.writeText(url).catch(() => {});
    toast.success("Profile link copied.");
  };

  // ── Location handlers ────────────────────────────────────────────────
  // Master switch. When turning OFF, also wipe lat/lng/city so the
  // profile doesn't keep a stale fix around. The text `location` field
  // (home-base label) is left alone — that's user-typed, not GPS.
  const toggleLocationEnabled = async (next: boolean) => {
    if (!user || savingLocationEnabled) return;
    setSavingLocationEnabled(true);
    setLocationEnabled(next);
    const patch: Record<string, unknown> = { location_enabled: next };
    if (!next) {
      patch.lat = null;
      patch.lng = null;
      patch.city = null;
    }
    const { error } = await supabase
      .from("profiles")
      .update(patch as never)
      .eq("id", user.id);
    setSavingLocationEnabled(false);
    if (error) {
      setLocationEnabled(!next);
      toast.error("Couldn't save preference");
      return;
    }
    if (!next) {
      setProfileLat(null);
      setProfileLng(null);
      setProfileCity(null);
      // Clear the geo cache + last-write marker so a future toggle-on
      // doesn't reuse a stale fix without an explicit re-capture.
      try { localStorage.removeItem("fig.geo.cache"); } catch { /* ignore */ }
      try { sessionStorage.removeItem("fig.profile.geo.lastWrite"); } catch { /* ignore */ }
    }
    toast.success(next ? "Location sharing on" : "Location sharing off");
  };

  const saveHomeBase = async () => {
    if (!user || savingHomeBase) return;
    const trimmed = homeBase.trim().slice(0, 80);
    if (trimmed === savedHomeBase) { toast.info("No changes"); return; }
    setSavingHomeBase(true);
    // Write to BOTH `location` (free-text label rendered on Profile) and
    // `city` (the field the Nearby feed matches scans against). They hold
    // the same typed string so the Nearby filter works off what the user
    // typed here — no GPS, no captured fix required.
    // Also clear any stale lat/lng/captured-city from the legacy GPS path
    // so we never blend two location sources.
    const { error } = await supabase
      .from("profiles")
      .update({
        location: trimmed || null,
        city: trimmed || null,
        lat: null,
        lng: null,
      } as never)
      .eq("id", user.id);
    setSavingHomeBase(false);
    if (error) { toast.error("Couldn't save"); return; }
    setSavedHomeBase(trimmed);
    setHomeBase(trimmed);
    setProfileCity(trimmed || null);
    setProfileLat(null);
    setProfileLng(null);
    toast.success(trimmed ? "Home base saved" : "Home base cleared");
  };

  // Captured-fix GPS flow removed. Home base is typed directly into
  // saveHomeBase (above), which writes to both `location` and `city`. The
  // device-geo path that used to live here was the source of the "captured
  // fix vs typed city" confusion the product wanted gone.

  // ── Privacy: training-data consent ───────────────────────────────────
  // Opt-in by default (false). Flipping on also records a consent row so
  // we have an audit trail for what the user agreed to and when.
  const toggleTraining = async (next: boolean) => {
    if (!user || savingTraining) return;
    setSavingTraining(true);
    setAllowTraining(next);
    const { error } = await supabase
      .from("profiles")
      .update({ allow_training_use: next } as unknown as Record<string, never>)
      .eq("id", user.id);
    if (!error) {
      await supabase.from("consents").insert({
        user_id: user.id,
        kind: "allow_training_use",
        granted: next,
        context: { source: "settings" },
      });
    }
    setSavingTraining(false);
    if (error) {
      setAllowTraining(!next);
      toast.error("Couldn't save preference");
    } else {
      toast.success(next ? "Thanks — Fig will learn from your scans." : "Off — your scans won't be used for training.");
    }
  };

  // Private account: when on, only followers can see your posts in the feed.
  const togglePrivate = async (next: boolean) => {
    if (!user || savingPrivate) return;
    setSavingPrivate(true);
    setIsPrivate(next);
    const { error } = await supabase
      .from("profiles")
      .update({ is_private: next } as unknown as Record<string, never>)
      .eq("id", user.id);
    setSavingPrivate(false);
    if (error) {
      setIsPrivate(!next);
      toast.error("Couldn't update privacy");
    } else {
      toast.success(next ? "Account is private — only followers see your posts." : "Account is public.");
    }
  };

  const goBack = () => {
    // Prefer history pop so the user lands where they came from; fall back
    // to the profile page so a deep-link visit still has a sensible exit.
    if (window.history.length > 1) nav(-1);
    else nav("/you");
  };

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100%", position: "relative" }}>
        {/* Header — matches the visual rhythm of the old sheet header. */}
        <div style={{
          padding: "18px 20px 12px",
          borderBottom: "1px dashed hsl(var(--sand-deep))",
          display: "flex", alignItems: "center", gap: 10,
          position: "relative", zIndex: 2,
        }}>
          <button
            onClick={goBack}
            aria-label="Back"
            style={{
              background: "none", border: "1px solid hsl(var(--ink))",
              padding: 6, cursor: "pointer", color: "hsl(var(--ink))",
              width: 32, height: 32,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}
          >
            {Icon.chev(16, "l")}
          </button>
          <div style={{ flex: 1 }}>
            <div className="caps" style={{ color: "hsl(var(--rust))" }}>Settings</div>
            <h1 style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 26,
              color: "hsl(var(--ink))", margin: "2px 0 0", lineHeight: 1.1,
            }}>
              Your account
            </h1>
          </div>
        </div>

        <div style={{ padding: "16px 20px 32px", position: "relative", zIndex: 2 }}>
          {/* ── Tab bar ─────────────────────────────────────────────── */}
          {/* Tabs: the active tab fills with the theme's ink color so the
              user always knows which section they're on. Inactive tabs
              carry only a 1px ink border so they still read as navigation
              chrome, not body text. */}
          <div
            role="tablist"
            aria-label="Settings sections"
            style={{
              display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 4,
              marginBottom: 18,
            }}
          >
            {([
              { id: "profile", label: "Profile" },
              { id: "theme",   label: "Theme" },
              { id: "tips",    label: "Tips" },
              { id: "account", label: "Account" },
              { id: "subs",    label: "Plans" },
            ] as const).map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  role="tab"
                  type="button"
                  aria-selected={active}
                  onClick={() => setTab(t.id)}
                  style={{
                    padding: "10px 6px",
                    background: active ? "hsl(var(--ink))" : "transparent",
                    color: active ? "hsl(var(--ink-fg))" : "hsl(var(--ink))",
                    border: "1px solid hsl(var(--ink))",
                    cursor: "pointer",
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 11,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    textAlign: "center",
                    fontWeight: active ? 700 : 500,
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* ── Theme ───────────────────────────────────────────────── */}
          {tab === "theme" && (
          <Section title="Theme">
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontSize: 12, color: "hsl(var(--ink-2))", marginBottom: 10, lineHeight: 1.35,
            }}>
              Make Fig yours. Pick a palette, customize the accent, and dial
              decorations to taste — every change applies instantly.
            </div>
            <ThemeStudio />
            {/* The full per-theme contrast report is a dev/QA tool — admin-only.
                Regular users get vision controls via /settings/accessibility-hub. */}
            {isAdmin === true && (
              <div style={{ marginTop: 14 }}>
                <button
                  type="button"
                  onClick={() => nav("/settings/accessibility")}
                  style={{
                    background: "transparent",
                    color: "hsl(var(--ink))",
                    border: "1px solid hsl(var(--ink))",
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase",
                  }}
                >
                  View accessibility report →
                </button>
              </div>
            )}
          </Section>
          )}

          {/* ── Tips — discovery surface for FIG's reference content ─── */}
          {tab === "tips" && (
          <Section title="Tips & resources">
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontSize: 12, color: "hsl(var(--ink-2))",
              marginBottom: 10, lineHeight: 1.35,
            }}>
              Everything FIG knows about vintage — what we authenticate,
              how to scan well, and how to use the app.
            </div>
            {([
              {
                path: "/brands",
                label: "Accepted brands",
                hint: "What FIG verifies — luxury, designer, mid-tier, and what we don't accept.",
              },
              {
                path: "/best-practices",
                label: "Field guide",
                hint: "Photo checklist + how to capture labels, construction, and condition so verdicts are sharp.",
              },
              {
                path: "/verdict-legend",
                label: "Verdict legend",
                hint: "What each verdict means — FIG Verified, Likely Vintage, Inconclusive, and the rest.",
              },
              {
                path: "/settings/moderator-application",
                label: "Verified collector",
                hint: "Help moderate fast fashion, vote on questionable verdicts, and earn monthly free scans. See if you're eligible.",
              },
              {
                path: "/faq",
                label: "FAQ",
                hint: "How verification, pricing, and the FIG community work.",
              },
              {
                path: "/whats-new",
                label: "What's new",
                hint: "Recent updates to FIG — new brands, features, accuracy tunings.",
              },
              {
                path: "/tutorial",
                label: "Replay tutorial",
                hint: "Walk through the Field Kit (12 steps) again.",
                state: { replay: true },
              },
            ] as const).map((row, i, arr) => (
              <button
                key={row.path}
                type="button"
                onClick={() => nav(row.path, "state" in row ? { state: row.state } : undefined)}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  width: "100%", padding: "12px 14px",
                  background: "hsl(var(--cream))",
                  border: "1px solid hsl(var(--ink))",
                  cursor: "pointer", textAlign: "left",
                  marginBottom: i === arr.length - 1 ? 10 : 10,
                }}
              >
                <div style={{ flex: 1, minWidth: 0, marginRight: 10 }}>
                  <div style={{
                    fontFamily: "'Fraunces', 'Playfair Display', serif",
                    fontWeight: 700, fontSize: 15, color: "hsl(var(--ink))",
                    lineHeight: 1.2,
                  }}>{row.label}</div>
                  <div style={{
                    fontFamily: "'Fraunces', 'Playfair Display', serif",
                    fontSize: 12, color: "hsl(var(--ink-2))",
                    marginTop: 3, lineHeight: 1.35,
                  }}>{row.hint}</div>
                </div>
                <span style={{ color: "hsl(var(--ink-2))", flexShrink: 0 }}>
                  {Icon.chev(14)}
                </span>
              </button>
            ))}

            {/* Combined Community + Support card — one visual unit, two
                tappable rows inside separated by a dashed divider. */}
            <div style={{
              background: "hsl(var(--cream))",
              border: "1px solid hsl(var(--ink))",
            }}>
              <button
                type="button"
                onClick={() => nav("/community-guidelines")}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  width: "100%", padding: "12px 14px",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer", textAlign: "left",
                }}
              >
                <div style={{ flex: 1, minWidth: 0, marginRight: 10 }}>
                  <div style={{
                    fontFamily: "'Fraunces', 'Playfair Display', serif",
                    fontWeight: 700, fontSize: 15, color: "hsl(var(--ink))",
                    lineHeight: 1.2,
                  }}>Community guidelines</div>
                  <div style={{
                    fontFamily: "'Fraunces', 'Playfair Display', serif",
                    fontSize: 12, color: "hsl(var(--ink-2))",
                    marginTop: 3, lineHeight: 1.35,
                  }}>How we expect FIG members to treat each other.</div>
                </div>
                <span style={{ color: "hsl(var(--ink-2))", flexShrink: 0 }}>
                  {Icon.chev(14)}
                </span>
              </button>
              <div style={{
                borderTop: "1px dashed hsl(var(--sand-deep))",
                margin: "0 14px",
              }} />
              <button
                type="button"
                onClick={() => nav("/support")}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  width: "100%", padding: "12px 14px",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer", textAlign: "left",
                }}
              >
                <div style={{ flex: 1, minWidth: 0, marginRight: 10 }}>
                  <div style={{
                    fontFamily: "'Fraunces', 'Playfair Display', serif",
                    fontWeight: 700, fontSize: 15, color: "hsl(var(--ink))",
                    lineHeight: 1.2,
                  }}>Contact support</div>
                  <div style={{
                    fontFamily: "'Fraunces', 'Playfair Display', serif",
                    fontSize: 12, color: "hsl(var(--ink-2))",
                    marginTop: 3, lineHeight: 1.35,
                  }}>Hit a bug or need help with a verification.</div>
                </div>
                <span style={{ color: "hsl(var(--ink-2))", flexShrink: 0 }}>
                  {Icon.chev(14)}
                </span>
              </button>
            </div>
          </Section>
          )}

          {/* ── Profile picture ─────────────────────────────────────── */}
          {tab === "profile" && (
          <>
          <Section title="Featured badges">
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontSize: 12, color: "hsl(var(--ink-2))",
              marginBottom: 10, lineHeight: 1.35,
            }}>
              Choose up to three badges to show on your profile hero.
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => nav("/you/badges")}
              style={{ width: "100%", padding: "10px", textAlign: "left" }}
            >
              Choose featured badges →
            </button>
          </Section>

          <Section title="Username">
            {!editingHandle ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <div style={{
                  display: "inline-flex", alignItems: "center",
                  padding: "8px 14px",
                  border: "1px solid hsl(var(--ink))",
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 14,
                  color: "hsl(var(--ink))",
                  background: "hsl(var(--paper-2))",
                }}>
                  @{savedHandle || "—"}
                </div>
                <button
                  type="button"
                  className="btn"
                  style={{ padding: "8px 14px" }}
                  onClick={() => { setHandle(savedHandle); setHandleStatus("idle"); setEditingHandle(true); }}
                >
                  Change
                </button>
              </div>
            ) : (
              <>
                <div style={{ position: "relative" }}>
                  <span style={{
                    position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 13,
                    color: "hsl(var(--ink-2))", pointerEvents: "none",
                  }}>@</span>
                  <input
                    type="text"
                    autoFocus
                    value={handle}
                    onChange={(e) => checkHandle(e.target.value)}
                    placeholder="yourname"
                    style={{
                      width: "100%", padding: "10px 12px 10px 26px",
                      border: `1px solid ${handleStatus === "taken" || handleStatus === "invalid" ? "hsl(var(--rust))" : handleStatus === "available" ? "hsl(var(--olive))" : "hsl(var(--ink))"}`,
                      background: "hsl(var(--paper))",
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: 13,
                      color: "hsl(var(--ink))", outline: "none", boxSizing: "border-box",
                    }}
                  />
                </div>
                {handleStatus !== "idle" && (
                  <div style={{
                    marginTop: 5, fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                    color: handleStatus === "available" ? "hsl(var(--olive))"
                      : handleStatus === "taken" || handleStatus === "invalid" ? "hsl(var(--rust))"
                      : "hsl(var(--ink-2))",
                  }}>
                    {handleStatus === "available" ? "✓ Available"
                      : handleStatus === "taken" ? "✗ Already taken"
                      : handleStatus === "invalid" ? "3–20 characters, letters, numbers and _ only"
                      : "Checking…"}
                  </div>
                )}
                <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    className="btn"
                    style={{ flex: 1, padding: "10px 12px" }}
                    onClick={() => { setHandle(savedHandle); setHandleStatus("idle"); setEditingHandle(false); }}
                    disabled={savingHandle}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveHandle}
                    disabled={savingHandle || handleStatus === "taken" || handleStatus === "invalid" || handleStatus === "checking"}
                    className="btn btn-primary"
                    style={{
                      flex: 1, padding: "10px 12px",
                      opacity: (savingHandle || handleStatus === "taken" || handleStatus === "invalid" || handleStatus === "checking") ? 0.5 : 1,
                      cursor: (savingHandle || handleStatus === "taken" || handleStatus === "invalid" || handleStatus === "checking") ? "not-allowed" : "pointer",
                    }}
                  >
                    {savingHandle ? "Saving…" : "Save"}
                  </button>
                </div>
              </>
            )}
          </Section>

          <Section title="Profile picture">
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {avatar && /^https?:\/\//.test(avatar) ? (
                <img
                  src={avatar}
                  alt=""
                  style={{
                    width: 56, height: 56, borderRadius: "50%",
                    border: "1.5px solid hsl(var(--ink))",
                    objectFit: "cover",
                    background: "hsl(var(--paper-3))",
                  }}
                />
              ) : (
                <div style={{
                  width: 56, height: 56, borderRadius: "50%",
                  border: "1.5px solid hsl(var(--ink))",
                  background: "hsl(var(--paper-3))",
                }} />
              )}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingAvatar}
                className="btn"
                style={{ flex: 1, padding: "10px 12px", cursor: uploadingAvatar ? "wait" : "pointer" }}
              >
                {uploadingAvatar ? "Uploading…" : "Upload new picture"}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) onPickAvatarFile(f);
                }}
              />
            </div>
          </Section>

          {/* ── Marketplace profiles ───────────────────────────────── */}
          <div id="shops" style={{ scrollMarginTop: 80 }}>
          <Section title="Your shops">
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontSize: 12, color: "hsl(var(--ink-2))", marginBottom: 10, lineHeight: 1.35,
            }}>
              Add links to your public shops. Anyone visiting your profile can tap through to buy from you.
            </div>

            {loaded && links.length === 0 && (
              <div style={{
                border: "1px dashed hsl(var(--ink))", padding: 14,
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontSize: 13, color: "hsl(var(--ink-2))", textAlign: "center",
              }}>
                No shops linked yet.
              </div>
            )}

            {links.map((l, idx) => {
              const lower = l.name.toLowerCase();
              const isInPerson = lower === "in person";
              const isPersonalSite = lower === "personal site";
              const placeholder = isInPerson
                ? "123 Main St, Brooklyn NY  or  https://maps.app.goo.gl/…"
                : isPersonalSite
                ? "https://yourshop.com"
                : `https://${lower}.com/your-shop`;
              const helper = isInPerson
                ? "Paste your address or a Google/Apple Maps share link."
                : isPersonalSite
                ? "Paste your full site URL."
                : "Paste your full shop URL.";
              // Skip domain warning for In Person + Personal Site — those
              // accept free-form input.
              const warn = isInPerson || isPersonalSite ? null : urlDomainWarning(l.name, l.url);
              return (
                <div key={l.name + idx} style={{
                  marginTop: idx === 0 ? 0 : 10,
                  border: "1px solid hsl(var(--ink))",
                  padding: "10px 12px",
                  background: "hsl(var(--paper-2, var(--cream)))",
                }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <div className="caps" style={{ color: "hsl(var(--ink))", fontSize: 11 }}>{l.name}</div>
                    <button
                      onClick={() => removeLink(idx)}
                      aria-label={`Remove ${l.name}`}
                      style={{
                        background: "transparent", border: "1px dashed hsl(var(--ink-2))",
                        padding: "3px 8px", cursor: "pointer",
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
                        letterSpacing: "0.1em", textTransform: "uppercase",
                        color: "hsl(var(--ink-2))",
                      }}
                    >Remove</button>
                  </div>
                  <input
                    type={isInPerson ? "text" : "url"}
                    inputMode={isInPerson ? "text" : "url"}
                    placeholder={placeholder}
                    value={l.url}
                    onChange={(e) => updateLinkUrl(idx, e.target.value)}
                    style={{
                      width: "100%", marginTop: 6,
                      background: "hsl(var(--cream))",
                      border: "1px solid hsl(var(--sand-deep))",
                      padding: "8px 10px",
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                      color: "hsl(var(--ink))",
                    }}
                  />
                  <div style={{
                    marginTop: 4, fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
                    color: warn ? "hsl(var(--rust))" : "hsl(var(--ink-2))",
                    letterSpacing: "0.06em",
                  }}>
                    {warn ?? helper}
                  </div>
                </div>
              );
            })}

            {remaining.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div className="caps" style={{ color: "hsl(var(--ink-2))", marginBottom: 6 }}>Add a shop</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {remaining.map((m) => (
                    <button
                      key={m}
                      onClick={() => addLink(m)}
                      style={{
                        background: "transparent", border: "1px solid hsl(var(--ink))",
                        padding: "6px 10px", cursor: "pointer",
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                        letterSpacing: "0.08em", textTransform: "uppercase",
                        color: "hsl(var(--ink))",
                      }}
                    >+ {m}</button>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={saveLinks}
              disabled={savingLinks}
              className="btn btn-primary"
              style={{
                width: "100%", marginTop: 14, padding: "10px",
                cursor: savingLinks ? "wait" : "pointer",
                opacity: savingLinks ? 0.6 : 1,
              }}
            >
              {savingLinks ? "Saving…" : "Save shops"}
            </button>
          </Section>
          </div>

          {/* ── Looking for (free-text wishlist) ─────────────────────
              5-10 short lines describing grails the collector is hunting
              for. Surfaced on the public postcard. Cap of 10 enforced
              client-side AND server-side via a CHECK constraint. */}
          <Section title="Looking for">
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontSize: 12, color: "hsl(var(--ink-2))", marginBottom: 10, lineHeight: 1.35,
            }}>
              Up to ten short lines. Other collectors see these on your profile postcard
              and can reach out if they spot one in the wild.
            </div>
            <LookingForEditor value={lookingFor} onChange={setLookingFor} />
            {(() => {
              const dirty = JSON.stringify(lookingFor) !== JSON.stringify(savedLookingFor);
              return (
                <button
                  className="btn btn-primary"
                  disabled={!dirty || savingLookingFor}
                  onClick={async () => {
                    if (!user) return;
                    setSavingLookingFor(true);
                    const { error } = await supabase
                      .from("profiles")
                      .update({ looking_for: lookingFor } as never)
                      .eq("id", user.id);
                    setSavingLookingFor(false);
                    if (error) { toast.error(error.message || "Couldn't save"); return; }
                    setSavedLookingFor(lookingFor);
                    toast.success("Wishlist saved");
                  }}
                  style={{
                    width: "100%", marginTop: 14, padding: "10px",
                    cursor: !dirty || savingLookingFor ? "not-allowed" : "pointer",
                    opacity: !dirty || savingLookingFor ? 0.5 : 1,
                  }}
                >
                  {savingLookingFor ? "Saving…" : "Save wishlist"}
                </button>
              );
            })()}
          </Section>

          {/* ── Your interests ───────────────────────────────────────
              Edit the interest tags that personalize your feed and what
              other collectors see on your profile. Opens the same editor
              used by the Profile page. */}
          <Section title="Your interests">
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontSize: 12, color: "hsl(var(--ink-2))", marginBottom: 10, lineHeight: 1.35,
            }}>
              These tune your feed and show on your profile so other collectors know what you hunt.
            </div>
            {interests.length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                {interests.map((t) => {
                  const known = INTERESTS.find((i) => i.value === t);
                  return (
                    <span key={t} style={{
                      padding: "4px 10px", border: "1px solid hsl(var(--ink))",
                      background: "hsl(var(--cream))",
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                      letterSpacing: "0.08em", textTransform: "uppercase",
                      color: "hsl(var(--ink))",
                    }}>{known?.label ?? t}</span>
                  );
                })}
              </div>
            ) : (
              <div style={{
                border: "1px dashed hsl(var(--ink))", padding: 14, marginBottom: 12,
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontSize: 13, color: "hsl(var(--ink-2))", textAlign: "center",
              }}>
                No interests picked yet.
              </div>
            )}
            <button
              onClick={() => setEditingInterests(true)}
              className="btn"
              style={{ width: "100%", padding: "10px" }}
            >
              {interests.length > 0 ? "Edit interests" : "Pick interests"}
            </button>
          </Section>

          {/* ── Email (kept on Profile tab so users can update sign-in) ── */}
          <Section title="Email">
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
              color: "hsl(var(--ink-2))", marginBottom: 8,
              wordBreak: "break-all",
            }}>
              Current: {user?.email ?? "—"}
            </div>
            <input
              type="email"
              inputMode="email"
              placeholder="new-email@example.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              style={{
                width: "100%",
                background: "hsl(var(--paper))",
                border: "1px solid hsl(var(--ink))",
                padding: "10px 12px",
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
                color: "hsl(var(--ink))",
                outline: "none",
              }}
            />
            <button
              onClick={updateEmail}
              disabled={updatingEmail || !newEmail.trim()}
              className="btn"
              style={{
                marginTop: 8, padding: "8px 12px",
                cursor: updatingEmail ? "wait" : "pointer",
                opacity: updatingEmail || !newEmail.trim() ? 0.6 : 1,
              }}
            >
              {updatingEmail ? "Sending…" : "Send confirmation link"}
            </button>
            <div style={{
              marginTop: 6, fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontSize: 11, color: "hsl(var(--ink-2))", lineHeight: 1.35,
            }}>
              Your email changes after you click the confirmation link we send to the new address.
            </div>
          </Section>
          </>
          )}

          {/* ── Accessibility ────────────────────────────────────────── */}
          {/* ── Subscriptions / Plans ───────────────────────────────── */}
          {tab === "subs" && (
          <>
            <Section title="FIG Verify plans">
              <div style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontSize: 12, color: "hsl(var(--ink-2))", marginBottom: 12, lineHeight: 1.35,
              }}>
                Every account starts with 5 free scans on the full Pro experience.
                Once they're used, choose a pack or subscribe to keep verifying.
              </div>
              <SubscriptionTiers />
            </Section>
          </>
          )}

          {/* ── Privacy / Got an idea / Account ─────────────────────────
                Order is intentional:
                  1. Privacy (notifications + location + private + training)
                  2. Got an idea? (suggestion box)
                  3. Account (share, support, legal, sign out, delete)
                Notifications used to live under Account; it's now grouped
                with the other personal-preference toggles. The suggestion
                box used to be embedded inside the Privacy section; it now
                stands on its own between Privacy and Account so it reads
                as a distinct prompt. */}
          {tab === "account" && (
          <>
          <Section title="Privacy">
            {/* Notifications preferences — lives in Privacy alongside the
                rest of the personal-preference toggles (location, private
                account, training-data). Tapping deep-links into the
                full per-channel preferences page. */}
            <div style={{ marginBottom: 14, paddingBottom: 14, borderBottom: "1px dashed hsl(var(--ink-2))" }}>
              <button
                onClick={() => nav("/settings/notifications")}
                className="btn"
                style={{ width: "100%", padding: "10px", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <span>
                  <span style={{ display: "block", fontWeight: 700 }}>Notifications</span>
                  <span style={{ display: "block", fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, opacity: 0.7, marginTop: 2 }}>
                    Preferences
                  </span>
                </span>
                <span aria-hidden style={{ fontFamily: "monospace", fontSize: 16 }}>›</span>
              </button>
            </div>
            {isAdmin === true && (
              <div style={{ marginBottom: 14, paddingBottom: 14, borderBottom: "1px dashed hsl(var(--ink-2))" }}>
                <button onClick={() => nav("/admin")} className="caps" style={{
                  padding: "10px 14px", background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))",
                  border: "1px solid hsl(var(--ink))", cursor: "pointer", width: "100%",
                }}>
                  Open admin console
                </button>
              </div>
            )}
            {/* Location — master toggle + home-base label + saved fix. */}
            <div style={{ marginBottom: 14, paddingBottom: 14, borderBottom: "1px dashed hsl(var(--ink-2))" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <input
                  type="checkbox"
                  id="location-enabled"
                  checked={locationEnabled}
                  disabled={savingLocationEnabled || !loaded}
                  onChange={(e) => toggleLocationEnabled(e.target.checked)}
                  style={{ marginTop: 3, accentColor: "hsl(var(--rust))" }}
                />
                <label htmlFor="location-enabled" style={{ flex: 1, cursor: "pointer" }}>
                  <div style={{
                    fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 14,
                    color: "hsl(var(--ink))",
                  }}>
                    Share my location
                  </div>
                  <div style={{
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                    color: "hsl(var(--ink-2))", lineHeight: 1.4, marginTop: 2,
                  }}>
                    Lets Fig surface nearby finds using the city you type below. No GPS — just match on city name.
                  </div>
                </label>
              </div>

              {locationEnabled && (
                <div style={{ marginTop: 12, paddingLeft: 26 }}>
                  {/* Home-base label — free-text "Brooklyn, NY" style. */}
                  <div style={{
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                    letterSpacing: "0.1em", textTransform: "uppercase",
                    color: "hsl(var(--ink-2))", marginBottom: 6,
                  }}>
                    Home base
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <input
                      type="text"
                      value={homeBase}
                      onChange={(e) => setHomeBase(e.target.value)}
                      placeholder="e.g. Brooklyn, NY"
                      maxLength={80}
                      style={{
                        flex: 1,
                        padding: "8px 10px",
                        border: "1px solid hsl(var(--ink))",
                        background: "hsl(var(--paper))",
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
                        color: "hsl(var(--ink))",
                        outline: "none",
                      }}
                    />
                    <button
                      type="button"
                      onClick={saveHomeBase}
                      disabled={savingHomeBase || homeBase.trim() === savedHomeBase}
                      className="btn"
                      style={{
                        padding: "8px 12px", fontSize: 11,
                        opacity: (savingHomeBase || homeBase.trim() === savedHomeBase) ? 0.5 : 1,
                      }}
                    >
                      {savingHomeBase ? "…" : "Save"}
                    </button>
                  </div>

                </div>
              )}
            </div>

            {/* Private account — when on, only followers see your posts. */}
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14, paddingBottom: 14, borderBottom: "1px dashed hsl(var(--ink-2))" }}>
              <input
                type="checkbox"
                id="private-account"
                checked={isPrivate}
                disabled={savingPrivate || !loaded}
                onChange={(e) => togglePrivate(e.target.checked)}
                style={{ marginTop: 3, accentColor: "hsl(var(--rust))" }}
              />
              <label htmlFor="private-account" style={{ flex: 1, cursor: "pointer" }}>
                <div style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 14,
                  color: "hsl(var(--ink))",
                }}>
                  Private account
                </div>
                <div style={{
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                  color: "hsl(var(--ink-2))", lineHeight: 1.4, marginTop: 2,
                }}>
                  Only your followers can see what you post. Your profile, name, and handle stay visible so people can still find and follow you.
                </div>
              </label>
            </div>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <input
                type="checkbox"
                id="allow-training"
                checked={allowTraining}
                disabled={savingTraining || !loaded}
                onChange={(e) => toggleTraining(e.target.checked)}
                style={{ marginTop: 3, accentColor: "hsl(var(--rust))" }}
              />
              <label htmlFor="allow-training" style={{ flex: 1, cursor: "pointer" }}>
                <div style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 14,
                  color: "hsl(var(--ink))",
                }}>
                  Help improve Fig
                </div>
                <div style={{
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                  color: "hsl(var(--ink-2))", lineHeight: 1.4, marginTop: 2,
                }}>
                  Allow your scans + verdict feedback to be used to make Fig sharper. We never share photos publicly. Off by default.
                </div>
              </label>
            </div>
          </Section>

          {/* ── Got an idea? — standalone section between Privacy and
                Account so the suggestion box reads as its own prompt
                rather than a footnote attached to privacy toggles. */}
          <Section title="Got an idea?">
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
              color: "hsl(var(--ink-2))", lineHeight: 1.4, marginBottom: 10,
            }}>
              Suggestions, bug reports, features you'd love — send it our way.
            </div>
            <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
              {(["idea", "bug", "other"] as const).map((c) => {
                const active = suggestionCategory === c;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setSuggestionCategory(c)}
                    className="caps"
                    style={{
                      flex: 1, padding: "6px 8px", cursor: "pointer",
                      border: "1px solid hsl(var(--ink))",
                      background: active ? "hsl(var(--ink))" : "transparent",
                      color: active ? "hsl(var(--ink-fg))" : "hsl(var(--ink))",
                      fontSize: 10,
                    }}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
            <textarea
              value={suggestionBody}
              onChange={(e) => setSuggestionBody(e.target.value)}
              maxLength={2000}
              rows={4}
              placeholder="What would make Fig better?"
              style={{
                width: "100%", padding: "10px 12px", resize: "vertical",
                border: "1px solid hsl(var(--ink))", background: "hsl(var(--paper))",
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
                color: "hsl(var(--ink))", outline: "none",
              }}
            />
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              marginTop: 6,
            }}>
              <span className="caps" style={{ color: "hsl(var(--ink-2))", fontSize: 9 }}>
                {suggestionBody.length}/2000
              </span>
              <button
                onClick={submitSuggestion}
                disabled={submittingSuggestion || suggestionBody.trim().length < 5}
                className="btn btn-primary"
                style={{
                  padding: "8px 14px",
                  cursor: submittingSuggestion ? "wait" : "pointer",
                  opacity: submittingSuggestion || suggestionBody.trim().length < 5 ? 0.6 : 1,
                }}
              >
                {submittingSuggestion ? "Sending…" : "Send suggestion"}
              </button>
            </div>
          </Section>

          <Section title="Account">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button
                onClick={handleShare}
                className="btn"
                style={{ width: "100%", padding: "10px" }}
              >
                Share your profile
              </button>
              <button
                onClick={() => nav("/support")}
                className="btn"
                style={{ width: "100%", padding: "10px" }}
              >
                Support
              </button>
              {modState?.status === 'eligible' && (
                <button
                  type="button"
                  onClick={() => nav("/settings/moderator-application")}
                  className="btn"
                  style={{ width: "100%", padding: "10px 14px", textAlign: "left" }}
                >
                  Become a fashion moderator →
                </button>
              )}
              {modState?.status === 'pending' && (
                <div style={{ width: "100%", padding: "10px 14px", opacity: 0.5, fontSize: 13 }}>
                  Moderator application pending
                </div>
              )}
              {modState?.status === 'approved' && (
                <div style={{ width: "100%", padding: "10px 14px", color: "hsl(var(--ink))", fontSize: 13 }}>
                  Verified moderator ✓
                </div>
              )}
              {modState?.status === 'rejected' && (
                <button
                  type="button"
                  onClick={() => nav("/settings/moderator-application")}
                  className="btn"
                  style={{ width: "100%", padding: "10px 14px", textAlign: "left", opacity: 0.7 }}
                >
                  Moderator application not approved
                </button>
              )}
              <button
                onClick={() => nav("/terms")}
                className="btn"
                style={{ width: "100%", padding: "10px" }}
              >
                Terms of service
              </button>
              <button
                onClick={() => nav("/privacy")}
                className="btn"
                style={{ width: "100%", padding: "10px" }}
              >
                Privacy policy
              </button>
              <button
                onClick={() => nav("/community-guidelines")}
                className="btn"
                style={{ width: "100%", padding: "10px" }}
              >
                Community guidelines
              </button>
              <button
                onClick={handleSignOut}
                style={{
                  width: "100%", padding: "10px",
                  background: "transparent",
                  border: "1px dashed hsl(var(--rust))",
                  color: "hsl(var(--rust))",
                  cursor: "pointer",
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                  letterSpacing: "0.1em", textTransform: "uppercase",
                }}
              >
                Sign out
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deletingAccount}
                style={{
                  width: "100%", padding: "10px",
                  background: "transparent",
                  border: "1px dashed hsl(var(--rust))",
                  color: "hsl(var(--rust))",
                  cursor: deletingAccount ? "wait" : "pointer",
                  opacity: deletingAccount ? 0.6 : 1,
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                  letterSpacing: "0.1em", textTransform: "uppercase",
                }}
              >
                {deletingAccount ? "Deleting…" : "Delete account"}
              </button>
            </div>
          </Section>
          </>
          )}
        </div>
      </div>


      {editingInterests && user && (
        <InterestsEditor
          userId={user.id}
          initial={interests}
          onClose={() => setEditingInterests(false)}
          onSaved={(tags) => { setInterests(tags); setEditingInterests(false); }}
        />
      )}
      {pendingAvatarFile && (
        <AvatarCropper
          file={pendingAvatarFile}
          saving={uploadingAvatar}
          onCancel={() => { if (!uploadingAvatar) setPendingAvatarFile(null); }}
          onConfirm={onAvatarConfirm}
        />
      )}
    </MobileFrame>
  );
}

// Tiny presentational wrapper so the section titles stay consistent.
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div className="caps" style={{
        color: "hsl(var(--rust))", marginBottom: 8,
        borderBottom: "1px solid hsl(var(--ink))", paddingBottom: 4,
        fontWeight: 600, fontSize: 13, letterSpacing: "0.12em",
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

